<template>
  <div class="warehouse-page">
    <div class="nav-bar-operations">
      <img src="@/assets/logogruas.png" alt="HomePage" @click="backMainPanel" class="logo-button" />
    </div>

    <div class="page-content">
      <div class="page-header">
        <div>
          <h2>Inventario de Piezas</h2>
          <p class="page-subtitle">{{ totalCount }} piezas registradas en almacén</p>
        </div>
        <div class="header-actions">
          <button v-if="canManage" class="action-button action-primary" @click="openCreateModal">Nueva pieza</button>
          <button class="action-button" @click="backWarehouse">Volver a Almacén</button>
        </div>
      </div>

      <div class="filters-card">
        <div class="filter-group">
          <label for="filter-part-name">Nombre</label>
          <input id="filter-part-name" type="text" v-model="filters.name" @keyup.enter="applyFilters" placeholder="Nombre o descripción" />
        </div>
        <div class="filter-group">
          <label for="filter-part-barcode">Código de barras</label>
          <input id="filter-part-barcode" type="text" v-model="filters.barcode" @keyup.enter="applyFilters" placeholder="Interno o de fabricante" />
        </div>
        <div class="filter-group">
          <label for="filter-part-category">Categoría</label>
          <select id="filter-part-category" v-model="filters.mechanical_category">
            <option value="">Todas</option>
            <option v-for="category in categories" :key="category.id" :value="category.id">
              {{ category.prefix }} — {{ category.name }}
            </option>
          </select>
        </div>
        <div class="filter-group">
          <label for="filter-part-brand">Marca</label>
          <input id="filter-part-brand" type="text" v-model="filters.brand" @keyup.enter="applyFilters" />
        </div>
        <div class="filter-group">
          <label for="filter-part-location">Ubicación</label>
          <input id="filter-part-location" type="text" v-model="filters.location" @keyup.enter="applyFilters" />
        </div>
        <div class="filter-group filter-group--checkbox">
          <label><input type="checkbox" v-model="filters.low_stock" /> Solo stock bajo</label>
        </div>
        <div class="filter-buttons">
          <button class="action-button action-primary" @click="applyFilters">Buscar</button>
          <button class="action-button" @click="clearFilters">Limpiar</button>
        </div>
      </div>

      <div class="table-card">
        <div class="table-scroll">
          <table class="data-table">
            <thead>
              <tr>
                <th :aria-sort="ariaSort('barcode')">
                  <button class="th-sort" @click="toggleSort('barcode')">
                    <span>Código</span>
                    <span class="th-sort__icon" :class="{ 'th-sort__icon--active': sortState.key === 'barcode' }">{{ sortIcon('barcode') }}</span>
                  </button>
                </th>
                <th :aria-sort="ariaSort('mechanical_category')">
                  <button class="th-sort" @click="toggleSort('mechanical_category')">
                    <span>Categoría</span>
                    <span class="th-sort__icon" :class="{ 'th-sort__icon--active': sortState.key === 'mechanical_category' }">{{ sortIcon('mechanical_category') }}</span>
                  </button>
                </th>
                <th :aria-sort="ariaSort('name')">
                  <button class="th-sort" @click="toggleSort('name')">
                    <span>Nombre</span>
                    <span class="th-sort__icon" :class="{ 'th-sort__icon--active': sortState.key === 'name' }">{{ sortIcon('name') }}</span>
                  </button>
                </th>
                <th>Marca pieza</th>
                <th :aria-sort="ariaSort('recipient_brand')">
                  <button class="th-sort" @click="toggleSort('recipient_brand')">
                    <span>Uso</span>
                    <span class="th-sort__icon" :class="{ 'th-sort__icon--active': sortState.key === 'recipient_brand' }">{{ sortIcon('recipient_brand') }}</span>
                  </button>
                </th>
                <th>Unidad</th>
                <th>Ubicación</th>
                <th class="col--num" :aria-sort="ariaSort('current_stock')">
                  <button class="th-sort" @click="toggleSort('current_stock')">
                    <span>Existencia</span>
                    <span class="th-sort__icon" :class="{ 'th-sort__icon--active': sortState.key === 'current_stock' }">{{ sortIcon('current_stock') }}</span>
                  </button>
                </th>
                <th class="col--num" :aria-sort="ariaSort('unit_cost')">
                  <button class="th-sort" @click="toggleSort('unit_cost')">
                    <span>Costo unitario</span>
                    <span class="th-sort__icon" :class="{ 'th-sort__icon--active': sortState.key === 'unit_cost' }">{{ sortIcon('unit_cost') }}</span>
                  </button>
                </th>
                <th class="col--num">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <template v-if="loading">
                <tr v-for="n in skeletonRows" :key="`skeleton-${n}`">
                  <td v-for="col in 10" :key="col"><span class="skeleton"></span></td>
                </tr>
              </template>
              <template v-else>
                <tr v-for="part in parts" :key="part.id" :class="{ 'row--low-stock': isLowStock(part) }">
                  <td><span class="barcode-pill" role="button" tabindex="0" @click="openBarcodeModal(part)" @keyup.enter="openBarcodeModal(part)">{{ part.barcode }}</span></td>
                  <td :class="{ 'cell-muted': !part.mechanical_category_name }">{{ part.mechanical_category_name || '—' }}</td>
                  <td class="cell-strong">{{ part.name }}</td>
                  <td :class="{ 'cell-muted': !part.brand }">{{ part.brand || '—' }}</td>
                  <td :class="{ 'cell-muted': !part.recipient_brand_name }">{{ part.recipient_brand_name || '—' }}</td>
                  <td :class="{ 'cell-muted': !part.unit_of_measure }">{{ part.unit_of_measure || '—' }}</td>
                  <td :class="{ 'cell-muted': !part.location }">{{ part.location || '—' }}</td>
                  <td class="col--num cell-strong">{{ formatNumber(part.current_stock) }}</td>
                  <td class="col--num">${{ formatNumber(part.unit_cost) }}</td>
                  <td class="col--num">
                    <div class="cell-actions">
                      <button v-if="canManage" class="mini-button" @click="openEditModal(part)">Editar</button>
                      <button class="mini-button" @click="openBarcodeModal(part)">Código</button>
                      <button v-if="canManage" class="mini-button mini-button--danger" :aria-label="`Eliminar ${part.name}`" @click="deletePart(part)">✕</button>
                    </div>
                  </td>
                </tr>
                <tr v-if="parts.length === 0">
                  <td colspan="10" class="empty-row">
                    <div class="empty-state">
                      <span class="empty-state__icon">📦</span>
                      <span class="empty-state__title">No se encontraron piezas</span>
                      <span class="empty-state__hint">Ajusta los filtros o registra una nueva pieza.</span>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>

        <div class="pagination-container">
          <button @click="goToPage(currentPage - 1)" :disabled="currentPage === 1" aria-label="Página anterior">‹</button>
          <span
            v-for="(pageNumber, index) in pageNumbers"
            :key="`${pageNumber}-${index}`"
            :class="{ active: pageNumber === currentPage, ellipsis: pageNumber === ellipsis }"
            @click="goToPage(pageNumber)"
          >
            {{ pageNumber }}
          </span>
          <button @click="goToPage(currentPage + 1)" :disabled="currentPage >= totalPages" aria-label="Página siguiente">›</button>
          <span class="pagination-summary">{{ totalCount }} resultados</span>
        </div>
      </div>
    </div>

    <!-- Create / Edit modal -->
    <div v-if="showFormModal" class="modal-overlay" @click.self="closeFormModal">
      <div class="modal-card" role="dialog" aria-modal="true" aria-labelledby="part-form-title">
        <h3 id="part-form-title">{{ editingPart ? `Editar pieza: ${editingPart.barcode}` : 'Nueva pieza' }}</h3>
        <div class="modal-body">
          <div class="modal-grid">
            <div class="form-input-group form-input-group--full">
              <label>Imagen</label>
              <div class="part-photo-wrap">
                <img v-if="partImageUrl" :src="partImageUrl" class="part-photo" alt="Imagen de la pieza" />
                <div v-else class="part-photo part-photo--empty">Sin imagen</div>
                <button type="button" class="action-button" @click="uploadPartImage" :disabled="uploadingImage">
                  {{ uploadingImage ? 'Subiendo...' : 'Subir imagen' }}
                </button>
              </div>
            </div>
            <div class="form-input-group">
              <label for="part-name">Nombre *</label>
              <input id="part-name" type="text" v-model="form.name" />
            </div>
            <div class="form-input-group">
              <label for="part-category">Categoría mecánica *</label>
              <select id="part-category" v-model="form.mechanical_category" :disabled="categoryLocked" required>
                <option value="" disabled>Seleccione</option>
                <option v-for="category in categories" :key="category.id" :value="category.id">
                  {{ category.prefix }} — {{ category.name }}
                </option>
              </select>
            </div>
            <div class="form-input-group">
              <label for="part-recipient-brand">Uso *</label>
              <select id="part-recipient-brand" v-model="form.recipient_brand" required>
                <option value="" disabled>Seleccione</option>
                <option v-for="brand in brands" :key="brand.id" :value="brand.id">
                  {{ brand.brand }}
                </option>
              </select>
            </div>
            <div class="form-input-group">
              <label for="part-brand">Marca de la pieza</label>
              <input id="part-brand" type="text" v-model="form.brand" />
            </div>
            <div class="form-input-group">
              <label for="part-unit">Unidad de medida</label>
              <select id="part-unit" v-model="form.unit_of_measure">
                <option value="">Seleccione</option>
                <option v-if="customUnit" :value="customUnit">{{ customUnit }}</option>
                <option v-for="unit in measurementUnits" :key="unit" :value="unit">{{ unit }}</option>
              </select>
            </div>
            <div class="form-input-group">
              <label for="part-location">Ubicación</label>
              <button id="part-location" type="button" class="location-pick" @click="openLocationMap">
                {{ form.location || 'Elegir en el mapa' }}
              </button>
            </div>
            <div class="form-input-group">
              <label for="part-mfr">Código de fabricante</label>
              <input id="part-mfr" type="text" v-model="form.manufacturer_barcode" placeholder="Escanear (opcional)" />
            </div>
            <div class="form-input-group">
              <label for="part-cost">Costo unitario *</label>
              <input id="part-cost" type="number" min="0" step="0.01" v-model.number="form.unit_cost" />
            </div>
            <div class="form-input-group">
              <label for="part-min">Stock mínimo</label>
              <input id="part-min" type="number" min="0" step="0.01" v-model.number="form.minimum_stock" />
            </div>
            <div v-if="!editingPart" class="form-input-group">
              <label for="part-stock">Existencia inicial</label>
              <input id="part-stock" type="number" min="0" step="0.01" v-model.number="form.current_stock" />
            </div>
            <div class="form-input-group form-input-group--full">
              <label for="part-desc">Descripción</label>
              <textarea id="part-desc" v-model="form.description" rows="3"></textarea>
            </div>
          </div>
        </div>
        <div class="modal-actions">
          <button class="action-button" @click="closeFormModal">Cancelar</button>
          <button class="action-button action-primary" @click="savePart" :disabled="saving">
            {{ saving ? 'Guardando...' : 'Guardar' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Barcode modal -->
    <div v-if="showBarcodeModal" class="modal-overlay" @click.self="closeBarcodeModal">
      <div class="modal-card modal-card--barcode" role="dialog" aria-modal="true" aria-labelledby="part-barcode-title">
        <h3 id="part-barcode-title">{{ barcodePart ? barcodePart.name : '' }}</h3>
        <div class="modal-body">
          <svg ref="barcodeSvg"></svg>
          <p class="barcode-caption">Etiqueta lista para imprimir en formato CODE128.</p>
        </div>
        <div class="modal-actions">
          <button class="action-button" @click="closeBarcodeModal">Cerrar</button>
          <button class="action-button" @click="printPartTicketLabel">Imprimir ticket</button>
          <button class="action-button action-primary" @click="printBarcode">Imprimir etiqueta</button>
        </div>
      </div>
    </div>

    <div v-if="showLocationMap" class="modal-overlay" @click.self="closeLocationMap">
      <div class="modal-card modal-card--wide" role="dialog" aria-modal="true" aria-labelledby="location-map-title">
        <h3 id="location-map-title">Ubicación en almacén</h3>
        <div class="modal-body location-map-body">
          <div class="floor-wrap">
            <div class="floor-map" ref="floorMap">
              <button
                v-for="shelf in shelves"
                :key="shelf.id"
                type="button"
                class="floor-shelf"
                :class="{
                  'floor-shelf--single': shelf.kind === 'single' && storesItems(shelf),
                  'floor-shelf--plain': shelf.stores_items === false,
                  'floor-shelf--active': activeShelf && activeShelf.id === shelf.id,
                  'floor-shelf--move': configuring,
                }"
                :style="shelfStyle(shelf)"
                @pointerdown="onShelfPointerDown($event, shelf)"
              >
                <span>{{ storesItems(shelf) ? shelf.code : (shelf.name || shelf.code) }}</span>
                <small v-if="storesItems(shelf) && shelf.name">{{ shelf.name }}</small>
                <span
                  v-if="configuring"
                  class="floor-shelf-resize"
                  @pointerdown.stop="onShelfResizeDown($event, shelf)"
                ></span>
              </button>
            </div>
          </div>
          <div class="location-side">
            <p v-if="!activeShelf" class="items-empty">Elige un estante en el mapa.</p>
            <template v-else>
              <h4 class="section-title">{{ activeShelf.code }} <span v-if="activeShelf.name">· {{ activeShelf.name }}</span></h4>
              <p v-if="activeShelf.stores_items === false" class="items-empty">Este objeto no guarda piezas. Puedes moverlo y cambiarlo en configuración.</p>
              <p v-else-if="activeShelf.kind === 'single'" class="items-empty">Este lugar tiene un solo identificador: {{ activeShelf.code }}.</p>
              <template v-else>
                <p class="items-empty">Nivel 1 es la fila de arriba. Sección 1 es la columna de la izquierda.</p>
                <div class="shelf-grid" :style="gridStyle(activeShelf)">
                  <button
                    v-for="cell in shelfCells(activeShelf)"
                    :key="cell.code"
                    type="button"
                    class="shelf-cell"
                    :class="{ 'shelf-cell--selected': form.location === cell.code }"
                    @click="chooseCell(cell.code)"
                  >
                    {{ cell.level }}-{{ cell.section }}
                  </button>
                </div>
              </template>
              <div v-if="canManage && configuring" class="modal-grid" style="margin-top:16px;">
                <div class="form-input-group">
                  <label for="shelf-name">Nombre</label>
                  <input id="shelf-name" type="text" v-model="shelfForm.name" />
                </div>
                <div class="form-input-group">
                  <label for="shelf-store">Guarda piezas</label>
                  <select id="shelf-store" v-model="shelfForm.stores_items">
                    <option :value="true">Sí</option>
                    <option :value="false">No</option>
                  </select>
                </div>
                <div class="form-input-group">
                  <label for="shelf-kind">Tipo</label>
                  <select id="shelf-kind" v-model="shelfForm.kind">
                    <option value="grid">Estante con niveles y secciones</option>
                    <option value="single">Lugar único</option>
                  </select>
                </div>
                <div v-if="shelfForm.kind === 'grid'" class="form-input-group">
                  <label for="shelf-levels">Niveles</label>
                  <input id="shelf-levels" type="number" min="1" max="20" v-model.number="shelfForm.levels" />
                </div>
                <div v-if="shelfForm.kind === 'grid'" class="form-input-group">
                  <label for="shelf-sections">Secciones</label>
                  <input id="shelf-sections" type="number" min="1" max="20" v-model.number="shelfForm.sections" />
                </div>
                <div class="form-input-group">
                  <label for="shelf-w">Ancho (%)</label>
                  <input id="shelf-w" type="number" min="4" max="80" step="0.5" v-model.number="shelfForm.map_w" />
                </div>
                <div class="form-input-group">
                  <label for="shelf-h">Alto (%)</label>
                  <input id="shelf-h" type="number" min="4" max="80" step="0.5" v-model.number="shelfForm.map_h" />
                </div>
                <p class="items-empty">Arrastra el objeto para moverlo. La esquina inferior derecha cambia el tamaño.</p>
              </div>
            </template>
            <div v-if="canManage && configuring && showNewShelf" class="modal-grid" style="margin-top:16px;">
                <div class="form-input-group">
                  <label for="new-shelf-code">Código *</label>
                  <input id="new-shelf-code" type="text" v-model="newShelf.code" maxlength="20" />
                </div>
                <div class="form-input-group">
                  <label for="new-shelf-name">Nombre</label>
                  <input id="new-shelf-name" type="text" v-model="newShelf.name" />
                </div>
                <div class="form-input-group">
                  <label for="new-shelf-store">Guarda piezas</label>
                  <select id="new-shelf-store" v-model="newShelf.stores_items">
                    <option :value="true">Sí</option>
                    <option :value="false">No</option>
                  </select>
                </div>
                <div class="form-input-group">
                  <label for="new-shelf-kind">Tipo</label>
                  <select id="new-shelf-kind" v-model="newShelf.kind">
                    <option value="grid">Estante con niveles y secciones</option>
                    <option value="single">Lugar único</option>
                  </select>
                </div>
              </div>
          </div>
        </div>
        <div class="modal-actions">
          <button class="action-button" @click="closeLocationMap">Cerrar</button>
          <button v-if="canManage" class="action-button" @click="configuring = !configuring">
            {{ configuring ? 'Terminar configuración' : 'Configurar mapa' }}
          </button>
          <button v-if="canManage && configuring && activeShelf" class="action-button action-primary" @click="saveShelf" :disabled="savingShelf">
            {{ savingShelf ? 'Guardando...' : 'Guardar objeto' }}
          </button>
          <button v-if="activeShelf && storesItems(activeShelf)" class="action-button" @click="printShelfLabel">Imprimir etiqueta</button>
          <button v-if="activeShelf && storesItems(activeShelf)" class="action-button" @click="printShelfTicketLabel">Imprimir ticket</button>
          <button v-if="canManage && configuring" class="action-button" @click="showNewShelf = !showNewShelf">Nuevo objeto</button>
          <button v-if="canManage && configuring && activeShelf" class="action-button" @click="deleteShelf" :disabled="savingShelf">Eliminar objeto</button>
          <button v-if="canManage && configuring && showNewShelf" class="action-button action-primary" @click="createShelf" :disabled="savingShelf">Crear</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import Swal from '@/utils/warehouseSwal'
import JsBarcode from 'jsbarcode'
import { printPartTicket, printShelfTicket } from '@/utils/printTicket'
import warehouseTable from '@/mixins/warehouseTable'

const MEASUREMENT_UNITS = [
  'Pieza',
  'Juego',
  'Par',
  'Kit',
  'Unidad',
  'Litro',
  'Galón',
  'Mililitro',
  'Lata',
  'Botella',
  'Cubeta',
  'Tambor',
  'Barril',
  'Garrafa',
  'Bidón',
  'Kilogramo',
  'Gramo',
  'Onza',
  'Metro',
  'Centímetro',
  'Pulgada',
  'Rollo',
  'Caja',
  'Bolsa',
  'Paquete',
  'Tubo',
  'Cartucho',
  'Docena',
]

export default {
  name: 'WarehouseParts',
  mixins: [warehouseTable],
  data() {
    return {
      parts: [],
      loading: false,
      saving: false,
      currentPage: 1,
      totalPages: 1,
      totalCount: 0,
      pageSize: 20,
      categories: [],
      brands: [],
      filters: {
        name: '',
        barcode: '',
        brand: '',
        mechanical_category: '',
        location: '',
        low_stock: false,
      },
      sortState: { key: '', dir: 'asc' },
      showFormModal: false,
      editingPart: null,
      form: this.emptyForm(),
      partImage: null,
      pendingImage: null,
      uploadingImage: false,
      showBarcodeModal: false,
      barcodePart: null,
      showLocationMap: false,
      shelves: [],
      activeShelf: null,
      configuring: false,
      showNewShelf: false,
      newShelf: { code: '', name: '', kind: 'grid', stores_items: true },
      savingShelf: false,
      shelfForm: { name: '', kind: 'grid', levels: 4, sections: 4, stores_items: true },
      permissions: [],
    }
  },
  computed: {
    canManage() {
      return this.hasPerm('maintenance.manage_parts')
    },
    categoryLocked() {
      return Boolean(this.editingPart && this.editingPart.mechanical_category)
    },
    partImageUrl() {
      return this.partImage?.preview_url || ''
    },
    measurementUnits() {
      return MEASUREMENT_UNITS
    },
    customUnit() {
      const current = (this.form.unit_of_measure || '').trim()
      if (!current || MEASUREMENT_UNITS.includes(current)) return ''
      return current
    },
  },
  created() {
    this.loadPermissions()
  },
  mounted() {
    this.fetchCatalogs()
    this.fetchParts(1)
  },
  methods: {
    emptyForm() {
      return {
        name: '',
        description: '',
        brand: '',
        mechanical_category: '',
        recipient_brand: '',
        unit_of_measure: '',
        location: '',
        manufacturer_barcode: '',
        unit_cost: null,
        minimum_stock: null,
        current_stock: null,
      }
    },
    loadPermissions() {
      try {
        this.permissions = JSON.parse(localStorage.getItem('userPermissions')) || []
      } catch (error) {
        this.permissions = []
      }
    },
    hasPerm(perm) {
      return this.permissions.includes(perm) || this.permissions.includes(`access_control.${perm}`)
    },
    backMainPanel() {
      this.$router.push('/main-panel')
    },
    backWarehouse() {
      this.$router.push('/warehouse')
    },
    formatNumber(value) {
      const num = Number(value)
      if (Number.isNaN(num)) return 'N/A'
      return num.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    isLowStock(part) {
      if (part.minimum_stock === null || part.minimum_stock === undefined) return false
      return Number(part.current_stock) <= Number(part.minimum_stock)
    },
    toggleSort(key) {
      if (this.sortState.key === key) {
        this.sortState.dir = this.sortState.dir === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortState = { key, dir: 'asc' }
      }
      this.fetchParts(1)
    },
    applyFilters() {
      this.fetchParts(1)
    },
    clearFilters() {
      this.filters = { name: '', barcode: '', brand: '', mechanical_category: '', location: '', low_stock: false }
      this.fetchParts(1)
    },
    goToPage(pageNumber) {
      if (typeof pageNumber !== 'number') return
      if (pageNumber < 1 || pageNumber > this.totalPages || pageNumber === this.currentPage) return
      this.fetchParts(pageNumber)
    },
    async fetchCatalogs() {
      const apiDirectory = process.env.API_DIRECTORY
      try {
        const [categoriesResponse, brandsResponse] = await Promise.all([
          axios.get(`${apiDirectory}/warehouse/mechanical-categories`),
          axios.get(`${apiDirectory}/warehouse/maintenance-brands`),
        ])
        this.categories = categoriesResponse.data.results || []
        this.brands = brandsResponse.data.results || []
      } catch (error) {
        console.error('Failed to fetch part catalogs:', error)
      }
    },
    async fetchParts(page) {
      const apiDirectory = process.env.API_DIRECTORY
      this.loading = true
      try {
        let orderingParam = ''
        if (this.sortState.key) {
          orderingParam = `&ordering=${this.sortState.dir === 'desc' ? '-' : ''}${this.sortState.key}`
        }
        const response = await axios.post(
          `${apiDirectory}/warehouse/get-parts-filtered?page=${page}&page_size=${this.pageSize}${orderingParam}`,
          this.filters,
          { headers: { 'Content-Type': 'application/json' } },
        )
        this.parts = response.data.results || []
        this.totalCount = response.data.count || 0
        this.totalPages = Math.max(1, Math.ceil(this.totalCount / this.pageSize))
        this.currentPage = page
      } catch (error) {
        console.error('Failed to fetch warehouse parts:', error)
        Swal.fire('Error', 'No se pudieron cargar las piezas.', 'error')
      } finally {
        this.loading = false
      }
    },
    openCreateModal() {
      this.editingPart = null
      this.form = this.emptyForm()
      this.clearPartImage()
      this.showFormModal = true
    },
    openEditModal(part) {
      this.editingPart = part
      this.form = {
        name: part.name || '',
        description: part.description || '',
        brand: part.brand || '',
        mechanical_category: part.mechanical_category || '',
        recipient_brand: part.recipient_brand || '',
        unit_of_measure: part.unit_of_measure || '',
        location: part.location || '',
        manufacturer_barcode: part.manufacturer_barcode || '',
        unit_cost: part.unit_cost !== null ? Number(part.unit_cost) : null,
        minimum_stock: part.minimum_stock !== null ? Number(part.minimum_stock) : null,
        current_stock: null,
      }
      this.clearPartImage()
      this.showFormModal = true
      this.fetchPartImage(part.id)
    },
    closeFormModal() {
      this.clearPartImage()
      this.showFormModal = false
    },
    openLocationMap() {
      this.showLocationMap = true
      this.configuring = false
      this.fetchShelves()
    },
    closeLocationMap() {
      this.showLocationMap = false
      this.activeShelf = null
    },
    async fetchShelves() {
      const apiDirectory = process.env.API_DIRECTORY
      try {
        const response = await axios.get(`${apiDirectory}/warehouse/shelves`)
        this.shelves = response.data.results || []
        this.syncActiveShelf()
      } catch (error) {
        console.error('Failed to fetch shelves:', error)
        Swal.fire('Error', 'No se pudo cargar el mapa del almacén.', 'error')
      }
    },
    shelfStyle(shelf) {
      return {
        left: `${shelf.map_x}%`,
        top: `${shelf.map_y}%`,
        width: `${shelf.map_w}%`,
        height: `${shelf.map_h}%`,
      }
    },
    shelfCells(shelf) {
      const cells = []
      for (let level = 1; level <= Number(shelf.levels); level += 1) {
        for (let section = 1; section <= Number(shelf.sections); section += 1) {
          cells.push({ level, section, code: `${shelf.code}-${level}-${section}` })
        }
      }
      return cells
    },
    gridStyle(shelf) {
      return { gridTemplateColumns: `repeat(${shelf.sections}, minmax(42px, 1fr))` }
    },
    storesItems(shelf) {
      return Boolean(shelf) && shelf.stores_items !== false
    },
    chooseShelf(shelf) {
      this.activeShelf = shelf
      this.shelfForm = {
        name: shelf.name || '',
        kind: shelf.kind,
        levels: Number(shelf.levels) || 4,
        sections: Number(shelf.sections) || 4,
        map_w: Number(shelf.map_w),
        map_h: Number(shelf.map_h),
        stores_items: this.storesItems(shelf),
      }
      if (!this.configuring && this.storesItems(shelf) && shelf.kind === 'single') {
        this.form.location = shelf.code
      }
    },
    chooseCell(code) {
      this.form.location = code
    },
    syncActiveShelf() {
      const location = this.form.location || ''
      const match = this.shelves.find((shelf) => location === shelf.code || location.startsWith(`${shelf.code}-`))
      if (match) this.chooseShelf(match)
    },
    async saveShelf() {
      if (!this.activeShelf) return
      const apiDirectory = process.env.API_DIRECTORY
      this.savingShelf = true
      try {
        const response = await axios.put(`${apiDirectory}/warehouse/shelves/${this.activeShelf.id}/`, {
          name: this.shelfForm.name || '',
          kind: this.shelfForm.kind,
          stores_items: this.shelfForm.stores_items === true || this.shelfForm.stores_items === 'true',
          levels: this.shelfForm.kind === 'grid' ? this.shelfForm.levels : 1,
          sections: this.shelfForm.kind === 'grid' ? this.shelfForm.sections : 1,
          map_w: this.shelfForm.map_w,
          map_h: this.shelfForm.map_h,
        }, { headers: { 'Content-Type': 'application/json' } })
        const index = this.shelves.findIndex((shelf) => shelf.id === this.activeShelf.id)
        if (index >= 0) this.shelves.splice(index, 1, response.data)
        this.activeShelf = response.data
        Swal.fire({
          toast: true,
          position: 'top-end',
          icon: 'success',
          background: '#d4edda',
          title: 'Estante actualizado',
          showConfirmButton: false,
          timer: 1600,
        })
      } catch (error) {
        const message = error.response?.data?.error
          || Object.values(error.response?.data || {}).flat().join(' ')
          || 'No se pudo guardar el estante.'
        Swal.fire('Error', message, 'error')
      } finally {
        this.savingShelf = false
      }
    },
    clampShelf(value, min, max) {
      return Math.round(Math.min(max, Math.max(min, value)) * 100) / 100
    },
    onShelfPointerDown(event, shelf) {
      if (!this.configuring || event.button !== 0) {
        if (event.button === 0) this.chooseShelf(shelf)
        return
      }
      const map = this.$refs.floorMap
      if (!map) return
      const rect = map.getBoundingClientRect()
      const startX = event.clientX
      const startY = event.clientY
      const originX = Number(shelf.map_x)
      const originY = Number(shelf.map_y)
      let moved = false
      const move = (ev) => {
        const dx = ((ev.clientX - startX) / rect.width) * 100
        const dy = ((ev.clientY - startY) / rect.height) * 100
        if (Math.abs(dx) + Math.abs(dy) > 0.4) moved = true
        shelf.map_x = this.clampShelf(originX + dx, 0, 100 - Number(shelf.map_w))
        shelf.map_y = this.clampShelf(originY + dy, 0, 100 - Number(shelf.map_h))
      }
      const up = async () => {
        window.removeEventListener('pointermove', move)
        window.removeEventListener('pointerup', up)
        if (!moved) {
          this.chooseShelf(shelf)
          return
        }
        await this.persistShelfBox(shelf)
      }
      window.addEventListener('pointermove', move)
      window.addEventListener('pointerup', up)
    },
    onShelfResizeDown(event, shelf) {
      if (!this.configuring || event.button !== 0) return
      const map = this.$refs.floorMap
      if (!map) return
      const rect = map.getBoundingClientRect()
      const startX = event.clientX
      const startY = event.clientY
      const originW = Number(shelf.map_w)
      const originH = Number(shelf.map_h)
      const move = (ev) => {
        const dx = ((ev.clientX - startX) / rect.width) * 100
        const dy = ((ev.clientY - startY) / rect.height) * 100
        shelf.map_w = this.clampShelf(originW + dx, 4, 100 - Number(shelf.map_x))
        shelf.map_h = this.clampShelf(originH + dy, 4, 100 - Number(shelf.map_y))
      }
      const up = async () => {
        window.removeEventListener('pointermove', move)
        window.removeEventListener('pointerup', up)
        this.chooseShelf(shelf)
        await this.persistShelfBox(shelf)
      }
      window.addEventListener('pointermove', move)
      window.addEventListener('pointerup', up)
    },
    async persistShelfBox(shelf) {
      const apiDirectory = process.env.API_DIRECTORY
      try {
        const response = await axios.put(`${apiDirectory}/warehouse/shelves/${shelf.id}/`, {
          map_x: shelf.map_x,
          map_y: shelf.map_y,
          map_w: shelf.map_w,
          map_h: shelf.map_h,
        }, { headers: { 'Content-Type': 'application/json' } })
        Object.assign(shelf, response.data)
        if (this.activeShelf && this.activeShelf.id === shelf.id) {
          this.shelfForm.map_w = Number(response.data.map_w)
          this.shelfForm.map_h = Number(response.data.map_h)
        }
      } catch (error) {
        console.error('Failed to move shelf:', error)
        Swal.fire('Error', 'No se pudo guardar la posición del estante.', 'error')
        this.fetchShelves()
      }
    },
    async createShelf() {
      const code = (this.newShelf.code || '').trim()
      if (!code) {
        Swal.fire('Falta información', 'El código del estante es obligatorio.', 'warning')
        return
      }
      const apiDirectory = process.env.API_DIRECTORY
      this.savingShelf = true
      try {
        const response = await axios.post(`${apiDirectory}/warehouse/shelves`, {
          code,
          name: this.newShelf.name || '',
          kind: this.newShelf.kind,
          stores_items: this.newShelf.stores_items === true || this.newShelf.stores_items === 'true',
          levels: this.newShelf.kind === 'grid' ? 4 : 1,
          sections: this.newShelf.kind === 'grid' ? 4 : 1,
          map_x: 36,
          map_y: 40,
          map_w: 14,
          map_h: 8,
        }, { headers: { 'Content-Type': 'application/json' } })
        this.shelves.push(response.data)
        this.chooseShelf(response.data)
        this.newShelf = { code: '', name: '', kind: 'grid', stores_items: true }
        this.showNewShelf = false
      } catch (error) {
        const message = error.response?.data?.error
          || error.response?.data?.code?.[0]
          || 'No se pudo crear el estante.'
        Swal.fire('Error', message, 'error')
      } finally {
        this.savingShelf = false
      }
    },
    async deleteShelf() {
      if (!this.activeShelf) return
      const result = await Swal.fire({
        title: 'Eliminar objeto',
        text: `Se quita ${this.activeShelf.name || this.activeShelf.code} del mapa. Las piezas que ya lo usan conservan su ubicación escrita.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Eliminar',
        cancelButtonText: 'Cancelar',
      })
      if (!result.isConfirmed) return
      const apiDirectory = process.env.API_DIRECTORY
      this.savingShelf = true
      try {
        await axios.delete(`${apiDirectory}/warehouse/shelves/${this.activeShelf.id}/`)
        this.shelves = this.shelves.filter((shelf) => shelf.id !== this.activeShelf.id)
        this.activeShelf = null
      } catch (error) {
        Swal.fire('Error', 'No se pudo eliminar el objeto.', 'error')
      } finally {
        this.savingShelf = false
      }
    },
    shelfBarcodeSvg(code) {
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
      document.body.appendChild(svg)
      try {
        JsBarcode(svg, code, {
          format: 'CODE128',
          displayValue: true,
          fontSize: 16,
          height: 70,
          margin: 10,
        })
        return svg.outerHTML
      } finally {
        svg.remove()
      }
    },
    printShelfLabel() {
      if (!this.activeShelf) return
      const printWindow = window.open('', '_blank', 'width=400,height=300')
      if (!printWindow) return
      const title = this.activeShelf.name || this.activeShelf.code
      const printScript = '<scr' + 'ipt>window.onload = function () { window.print(); window.close(); }</scr' + 'ipt>'
      printWindow.document.write(`
        <html>
          <head><title>${this.activeShelf.code}</title></head>
          <body style="text-align:center;font-family:sans-serif;">
            <p style="margin:4px 0;font-size:12px;">${title}</p>
            ${this.shelfBarcodeSvg(this.activeShelf.code)}
            ${printScript}
          </body>
        </html>
      `)
      printWindow.document.close()
    },
    printShelfTicketLabel() {
      printShelfTicket(this.activeShelf)
    },
    clearPartImage() {
      if (this.pendingImage?.preview_url) {
        URL.revokeObjectURL(this.pendingImage.preview_url)
      }
      this.pendingImage = null
      this.partImage = null
    },
    async fetchPartImage(partId) {
      const apiDirectory = process.env.API_DIRECTORY
      try {
        const response = await axios.get(`${apiDirectory}/get-files/`, {
          params: { object_type: 'warehouse_part', object_id: partId },
        })
        const files = Array.isArray(response.data) ? response.data : []
        let image = null
        files.forEach((file) => {
          const metadata = file.metadata || {}
          const mimeType = metadata.file_type || ''
          if ((metadata.category || '') !== 'part_image') return
          image = {
            file_id: file.file_id,
            preview_url: `data:${mimeType || 'image/jpeg'};base64,${file.content}`,
          }
        })
        if (!this.editingPart || this.editingPart.id !== partId) return
        this.partImage = image
      } catch (error) {
        if (!this.editingPart || this.editingPart.id !== partId) return
        if (error?.response?.status !== 404) console.error('Failed to fetch part image:', error)
        this.partImage = null
      }
    },
    async postPartImage(partId, file, metadata) {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('object_type', 'warehouse_part')
      formData.append('object_id', String(partId))
      formData.append('object_metadata', JSON.stringify({
        category: 'part_image',
        title: metadata.title,
        description: metadata.description || '',
        file_name: file.name,
        file_type: file.type,
      }))
      const apiDirectory = process.env.API_DIRECTORY
      await axios.post(`${apiDirectory}/upload-file`, formData)
    },
    async uploadPartImage() {
      try {
        const file = await new Promise((resolve) => {
          const input = document.createElement('input')
          input.type = 'file'
          input.accept = 'image/*'
          input.onchange = (event) => resolve(event.target.files?.[0] || null)
          input.click()
        })
        if (!file) return
        if (file.type && !file.type.startsWith('image/')) {
          Swal.fire('Archivo no válido', 'Selecciona una imagen.', 'warning')
          return
        }

        const defaultTitle = 'Imagen de pieza'
        const metadataPrompt = await Swal.fire({
          title: 'Detalles del archivo',
          html: `
            <input id="part-image-title" class="swal2-input" placeholder="Título" value="${defaultTitle}">
            <textarea id="part-image-description" class="swal2-textarea" placeholder="Descripción"></textarea>
          `,
          focusConfirm: false,
          showCancelButton: true,
          confirmButtonText: 'Subir',
          cancelButtonText: 'Cancelar',
          preConfirm: () => {
            const title = document.getElementById('part-image-title')?.value?.trim() || ''
            const description = document.getElementById('part-image-description')?.value?.trim() || ''
            if (!title) {
              Swal.showValidationMessage('El título es obligatorio')
              return null
            }
            return { title, description }
          },
        })
        if (!metadataPrompt.isConfirmed || !metadataPrompt.value) return

        if (this.editingPart) {
          this.uploadingImage = true
          await this.postPartImage(this.editingPart.id, file, metadataPrompt.value)
          await this.fetchPartImage(this.editingPart.id)
          Swal.fire('Completado', 'Imagen subida correctamente.', 'success')
          return
        }

        if (this.pendingImage?.preview_url) {
          URL.revokeObjectURL(this.pendingImage.preview_url)
        }
        const previewUrl = URL.createObjectURL(file)
        this.pendingImage = {
          file,
          title: metadataPrompt.value.title,
          description: metadataPrompt.value.description,
          preview_url: previewUrl,
        }
        this.partImage = { preview_url: previewUrl }
      } catch (error) {
        console.error('Failed to upload part image:', error)
        Swal.fire('Error', 'No se pudo subir la imagen.', 'error')
      } finally {
        this.uploadingImage = false
      }
    },
    async savePart() {
      const apiDirectory = process.env.API_DIRECTORY
      if (!this.form.name || !this.form.name.trim()) {
        Swal.fire('Falta información', 'El nombre de la pieza es obligatorio.', 'warning')
        return
      }
      if (!this.editingPart && !this.form.mechanical_category) {
        Swal.fire('Falta información', 'La categoría mecánica es obligatoria.', 'warning')
        return
      }
      if (!this.editingPart && !this.form.recipient_brand) {
        Swal.fire('Falta información', 'El uso es obligatorio.', 'warning')
        return
      }
      if (this.form.unit_cost === null || this.form.unit_cost === '' || Number.isNaN(Number(this.form.unit_cost))) {
        Swal.fire('Falta información', 'El costo unitario es obligatorio.', 'warning')
        return
      }
      const payload = {
        name: this.form.name.trim(),
        description: this.form.description || null,
        brand: this.form.brand || null,
        mechanical_category: this.form.mechanical_category || null,
        recipient_brand: this.form.recipient_brand || null,
        unit_of_measure: this.form.unit_of_measure || null,
        location: this.form.location || null,
        manufacturer_barcode: this.form.manufacturer_barcode ? this.form.manufacturer_barcode.trim() : null,
        unit_cost: this.form.unit_cost,
        minimum_stock: this.form.minimum_stock === '' ? null : this.form.minimum_stock,
      }
      this.saving = true
      try {
        if (this.editingPart) {
          await axios.put(`${apiDirectory}/warehouse/parts/${this.editingPart.id}/`, payload, {
            headers: { 'Content-Type': 'application/json' },
          })
        } else {
          payload.current_stock = this.form.current_stock || 0
          const created = await axios.post(`${apiDirectory}/warehouse/create-part`, payload, {
            headers: { 'Content-Type': 'application/json' },
          })
          if (this.pendingImage) {
            try {
              await this.postPartImage(created.data.id, this.pendingImage.file, this.pendingImage)
            } catch (imageError) {
              console.error('Failed to upload part image:', imageError)
              this.clearPartImage()
              this.showFormModal = false
              await Swal.fire('Pieza guardada', 'La pieza se guardó, pero no se pudo subir la imagen.', 'warning')
              this.fetchParts(this.currentPage)
              return
            }
          }
        }
        this.clearPartImage()
        this.showFormModal = false
        await Swal.fire({
          toast: true,
          position: 'top-end',
          icon: 'success',
          background: '#d4edda',
          title: '¡Guardado!',
          showConfirmButton: false,
          timer: 2000,
        })
        this.fetchParts(this.currentPage)
      } catch (error) {
        console.error('Failed to save part:', error)
        const message = error.response?.data?.error
          || Object.values(error.response?.data || {}).flat().join(' ')
          || 'No se pudo guardar la pieza.'
        Swal.fire('Error', message, 'error')
      } finally {
        this.saving = false
      }
    },
    async deletePart(part) {
      const result = await Swal.fire({
        title: '¿Eliminar pieza?',
        text: `Se eliminará "${part.name}" (${part.barcode}) del inventario.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sí, eliminar',
        cancelButtonText: 'Cancelar',
      })
      if (!result.isConfirmed) return
      const apiDirectory = process.env.API_DIRECTORY
      try {
        await axios.delete(`${apiDirectory}/warehouse/parts/${part.id}/`)
        this.fetchParts(this.currentPage)
      } catch (error) {
        console.error('Failed to delete part:', error)
        Swal.fire('Error', 'No se pudo eliminar la pieza.', 'error')
      }
    },
    openBarcodeModal(part) {
      this.barcodePart = part
      this.showBarcodeModal = true
      this.$nextTick(() => {
        if (this.$refs.barcodeSvg) {
          JsBarcode(this.$refs.barcodeSvg, part.barcode, {
            format: 'CODE128',
            displayValue: true,
            fontSize: 16,
            height: 70,
            margin: 10,
          })
        }
      })
    },
    closeBarcodeModal() {
      this.showBarcodeModal = false
      this.barcodePart = null
    },
    printPartTicketLabel() {
      printPartTicket(this.barcodePart)
    },
    printBarcode() {
      if (!this.$refs.barcodeSvg || !this.barcodePart) return
      const svgMarkup = this.$refs.barcodeSvg.outerHTML
      const printWindow = window.open('', '_blank', 'width=400,height=300')
      if (!printWindow) return
      const printScript = '<scr' + 'ipt>window.onload = function () { window.print(); window.close(); }</scr' + 'ipt>'
      printWindow.document.write(`
        <html>
          <head><title>${this.barcodePart.barcode}</title></head>
          <body style="text-align:center;font-family:sans-serif;">
            <p style="margin:4px 0;font-size:12px;">${this.barcodePart.name}</p>
            ${svgMarkup}
            ${printScript}
          </body>
        </html>
      `)
      printWindow.document.close()
    },
  },
}
</script>

<style scoped src="@/styles/warehouse.css"></style>
